// ═══════════════════════════════════════════════════════════════════════════
// Mi cuenta — lo del vendedor, que es suyo
//
// Sus datos y sus comisiones. Nadie más ve esto: ni sus compañeros ni —en el
// caso del CLABE— la gerencia puede modificarlo. Si se equivocó al capturar
// a dónde va su dinero, lo corrige él.
//
// LOS TRES NÚMEROS Y POR QUÉ SON TRES:
//
//   acreditada       la venta ya es suya; el cliente todavía no paga
//   por acreditarse  el cliente ya pagó y a él no. Es el número que importa
//   liquidada        ya se le depositó
//
// Son las palabras del operador, no una traducción: es el vocabulario con el
// que ya habla con sus vendedores.
//
// Un solo total escondería justo la diferencia que provoca los reclamos.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { misComisiones, miPerfil, guardarMiPerfil, clabeValido } from "../datos/vendedores.js";
import { COMISION } from "../dominio/estado-comision.js";
import { cambiarContrasena, LARGO_MINIMO } from "../auth/sesion.js";
import { crearLlave, misLlaves, revocarLlave } from "../auth/llaves.js";
import { estadoPin, ponerPin } from "../auth/pin.js";
import { fmt } from "../pdf/comunes.js";
import { Card, Label, SectionTitle } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle } from "./estilos.js";

export function MiCuenta({ sesion, membresia }) {
  const userId = sesion?.user?.id;

  const [perfil, setPerfil] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", clabe: "", bank_name: "", rfc: "" });
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState("");

  // Cambiar la contraseña SIN pasar por el correo. Estando dentro no hace
  // falta el rodeo, y el correo es justo lo que falla cuando más urge.
  //
  // El formulario NACE CERRADO. Dos campos de contraseña siempre a la vista,
  // en la misma pantalla donde se editan el CLABE y el RFC, invitan a teclear
  // ahí sin querer: se cambia una contraseña creyendo que se llenó otra cosa.
  // Hay que pedirlo.
  const [pidiendoPwd, setPidiendoPwd] = useState(false);
  const [pwd, setPwd] = useState({ uno: "", dos: "", ocupado: false, listo: false, error: "" });

  const [lineas, setLineas] = useState([]);
  const [abierta, setAbierta] = useState(null);      // tenant_id con el detalle abierto

  const cargar = useCallback(async () => {
    setError("");
    try {
      const [p, c] = await Promise.all([miPerfil(userId), misComisiones()]);
      if (p) setPerfil(p);
      setForm({
        full_name: p?.full_name || "", phone: p?.phone || "",
        clabe: p?.clabe || "", bank_name: p?.bank_name || "", rfc: p?.rfc || "",
      });
      setLineas(c || []);
    } catch (e) { setError(e.message); }
  }, [userId]);

  useEffect(() => { if (userId) cargar(); }, [userId, cargar]);

  const guardar = async () => {
    if (form.clabe && !clabeValido(form.clabe)) {
      setError("El CLABE son exactamente 18 dígitos."); return;
    }
    setGuardando(true); setError("");
    try {
      const limpio = {
        ...form,
        clabe: form.clabe ? form.clabe.replace(/\s/g, "") : null,
        full_name: form.full_name || null, phone: form.phone || null,
        bank_name: form.bank_name || null, rfc: form.rfc || null,
      };
      setPerfil(await guardarMiPerfil(userId, limpio));
      setGuardado(true); setTimeout(() => setGuardado(false), 2500);
    } catch (e) { setError(e.message); }
    setGuardando(false);
  };

  // ── Abrir y cerrar, nada más ──────────────────────────────────────────────
  //
  // Aquí vivía el detalle de remisiones con su ordenado por estado. Se mudó a
  // "Mis ventas", donde cada renglón dice ADEMÁS si se timbró y si el cliente
  // pagó — que es lo que el vendedor pregunta de verdad y aquí nunca estuvo.
  //
  // Decisión del operador: "coincido que mi cuenta debe tener solo información
  // personal y de nada más". Lo que queda es cuánto se le debe y sus datos.
  const alternar = (l) => setAbierta(a => (a === l.tenant_id ? null : l.tenant_id));

  const porComision = lineas.filter(l => l.scheme === "comision");
  const totalDebido = porComision.reduce((t, l) => t + Number(l.saldo || 0), 0);

  return (
    <div>
      <SectionTitle sub={sesion?.user?.email}>Mi cuenta</SectionTitle>

      {error && (
        <Card style={{ borderColor: "#3a1a1a" }}>
          <div style={{ color: "#f87171", fontSize: 13 }}>⚠️ {error}</div>
        </Card>
      )}

      {/* ── Comisiones ─────────────────────────────────────────────────── */}
      {porComision.length > 0 && (
        <Card>
          <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 4 }}>
            💰 MIS COMISIONES
          </div>
          <div style={{ fontSize: 26, color: totalDebido > 0 ? "#a8d060" : "#666",
                        fontWeight: 600, margin: "6px 0 2px" }}>
            {fmt(totalDebido)}
          </div>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 14 }}>
            por acreditarse — el cliente ya pagó y a ti todavía no
          </div>

          {lineas.map(l => {
            const estaAbierta = abierta === l.tenant_id;

            return (
            <div key={l.tenant_id} style={{ borderTop: "1px solid #1a1a1a", padding: "12px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {l.scheme === "comision" ? (
                  <button type="button" onClick={() => alternar(l)}
                    title={estaAbierta ? "Cerrar el detalle" : "Ver las remisiones"}
                    style={{ flex: 1, minWidth: 160, display: "flex", alignItems: "center",
                             gap: 8, background: "transparent", border: "none", padding: "2px 0",
                             textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                    <span style={{ fontSize: 10, color: "#666", width: 9 }}>
                      {estaAbierta ? "▾" : "▸"}
                    </span>
                    <span>
                      <span style={{ display: "block", fontSize: 14, color: "#e8e0d4" }}>
                        {l.vinicola}
                      </span>
                      <span style={{ display: "block", fontSize: 11, color: "#666" }}>
                        {l.remisiones} remisión{Number(l.remisiones) !== 1 ? "es" : ""}
                      </span>
                    </span>
                  </button>
                ) : (
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 14, color: "#e8e0d4" }}>{l.vinicola}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>
                      sueldo fijo — tus ventas no generan comisión
                    </div>
                  </div>
                )}

                {l.scheme === "comision" && (
                  <>
                    <Cifra etq="acreditada" v={l.por_cobrar} color="#8a8a6a"
                      titulo={COMISION.acreditada.ayuda} />
                    <Cifra etq="por acreditarse" v={l.saldo}
                      color={Number(l.saldo) > 0 ? "#a8d060" : "#555"}
                      titulo={COMISION.por_acreditarse.ayuda} />
                    <Cifra etq="liquidada" v={l.pagado} color="#666"
                      titulo={COMISION.liquidada.ayuda} />
                  </>
                )}
              </div>

              {abierta === l.tenant_id && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #141414",
                              fontSize: 11, color: "#666", lineHeight: 1.7 }}>
                  {membresia?.tenant_id !== l.tenant_id ? (
                    <>Para ver el detalle de esta vinícola, entra a ella.</>
                  ) : (
                    <>
                      El detalle —qué remisión, de qué cliente, cuánto y en qué
                      estado— está en <b style={{ color: "#a8d060" }}>Mis ventas</b>,
                      junto con si ya se timbró y si el cliente ya pagó. Aquí solo
                      viven tus datos.
                    </>
                  )}
                </div>
              )}
            </div>
            );
          })}

          <div style={{ fontSize: 10, color: "#556", lineHeight: 1.7, marginTop: 12,
                        paddingTop: 10, borderTop: "1px solid #1a1a1a" }}>
            <b>Acreditada</b>: la venta ya es tuya, el cliente aún no paga.
            <b> Por acreditarse</b>: ya pagó y falta que te depositen.
            <b> Liquidada</b>: ya te depositaron.
            <br />
            Se calcula sobre el total <b>con impuestos</b> de cada remisión y cubre
            también el reparto.
          </div>
        </Card>
      )}

      {/* ── Contraseña ─────────────────────────────────────────────────── */}
      <Card>
        <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>
          🔑 CONTRASEÑA
        </div>
        <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, marginBottom: 14 }}>
          Cámbiala cuando quieras, sin esperar ningún correo. Al menos {LARGO_MINIMO} caracteres.
        </div>

        {!pidiendoPwd ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button"
              onClick={() => { setPidiendoPwd(true);
                               setPwd({ uno: "", dos: "", ocupado: false, listo: false, error: "" }); }}
              style={{ ...btnSecondary, padding: "9px 18px", fontSize: 12 }}>
              Cambiar mi contraseña
            </button>
            {pwd.listo && (
              <span style={{ fontSize: 12, color: "#a8d060" }}>
                ✓ Cambiada. La nueva es la que sirve desde ahora.
              </span>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ minWidth: 180 }}>
                <Label>Nueva</Label>
                <input type="password" value={pwd.uno} autoComplete="new-password" autoFocus
                  onChange={e => setPwd(p => ({ ...p, uno: e.target.value, error: "", listo: false }))}
                  style={inputStyle} />
              </div>
              <div style={{ minWidth: 180 }}>
                <Label>Otra vez</Label>
                <input type="password" value={pwd.dos} autoComplete="new-password"
                  onChange={e => setPwd(p => ({ ...p, dos: e.target.value, error: "", listo: false }))}
                  style={{ ...inputStyle,
                           borderColor: pwd.dos && pwd.uno !== pwd.dos ? "#3a1a1a" : undefined }} />
              </div>
              <button
                disabled={pwd.ocupado || pwd.uno.length < LARGO_MINIMO || pwd.uno !== pwd.dos}
                onClick={async () => {
                  setPwd(p => ({ ...p, ocupado: true, error: "" }));
                  try {
                    await cambiarContrasena(pwd.uno);
                    // Se cierra al terminar: los campos no se quedan a la vista
                    // con la contraseña recién puesta.
                    setPwd({ uno: "", dos: "", ocupado: false, listo: true, error: "" });
                    setPidiendoPwd(false);
                  } catch (e) {
                    setPwd(p => ({ ...p, ocupado: false, error: e.message }));
                  }
                }}
                style={{ ...btnSecondary, padding: "9px 18px", fontSize: 12,
                         opacity: (pwd.ocupado || pwd.uno.length < LARGO_MINIMO || pwd.uno !== pwd.dos) ? 0.4 : 1 }}>
                {pwd.ocupado ? "Guardando…" : "Cambiar"}
              </button>
              <button type="button" disabled={pwd.ocupado}
                onClick={() => { setPidiendoPwd(false);
                                 setPwd({ uno: "", dos: "", ocupado: false, listo: false, error: "" }); }}
                style={{ background: "transparent", border: "1px solid #222", borderRadius: 6,
                         color: "#888", fontSize: 12, padding: "9px 16px", cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
            {pwd.dos && pwd.uno !== pwd.dos && (
              <div style={{ fontSize: 10, color: "#c97070", marginTop: 6 }}>No coinciden</div>
            )}
            {pwd.error && (
              <div style={{ fontSize: 12, color: "#f87171", marginTop: 8 }}>⚠️ {pwd.error}</div>
            )}
          </>
        )}
      </Card>

      {/* ── PIN ────────────────────────────────────────────────────────── */}
      <MiPin />

      {/* ── Llaves de acceso ───────────────────────────────────────────── */}
      <LlavesDeAcceso />

      {/* ── Mis datos ──────────────────────────────────────────────────── */}
      <Card>
        <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>
          👤 MIS DATOS
        </div>
        <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, marginBottom: 14 }}>
          Son tuyos, no de la vinícola: te siguen si trabajas con más de una.
          La vinícola los <b>ve</b> para poder depositarte, pero <b>no puede cambiarlos</b>.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                      gap: 12, marginBottom: 14 }}>
          <div>
            <Label>Nombre completo</Label>
            <input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
              style={inputStyle} />
          </div>
          <div>
            <Label>Teléfono</Label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              style={inputStyle} />
          </div>
          <div>
            <Label>CLABE (18 dígitos)</Label>
            <input value={form.clabe} inputMode="numeric"
              onChange={e => setForm({ ...form, clabe: e.target.value.replace(/[^\d\s]/g, "") })}
              placeholder="012180012345678901"
              style={{ ...inputStyle, fontFamily: "monospace",
                       borderColor: form.clabe && !clabeValido(form.clabe) ? "#3a1a1a" : undefined }} />
            {form.clabe && !clabeValido(form.clabe) && (
              <div style={{ fontSize: 10, color: "#c97070", marginTop: 3 }}>
                Van {form.clabe.replace(/\s/g, "").length} de 18 dígitos
              </div>
            )}
          </div>
          <div>
            <Label>Banco</Label>
            <input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })}
              style={inputStyle} />
          </div>
          <div>
            <Label>RFC (si facturas tus honorarios)</Label>
            <input value={form.rfc} onChange={e => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
              style={inputStyle} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={guardar} disabled={guardando}
            style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, opacity: guardando ? 0.5 : 1 }}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
          {guardado && <span style={{ fontSize: 12, color: "#a8d060" }}>✓ Guardado</span>}
          {!perfil?.clabe && porComision.length > 0 && (
            <span style={{ fontSize: 11, color: "#facc15" }}>
              Sin CLABE no te pueden depositar.
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}

/** Una cifra con su ayuda. Ordenar se lo llevó "Mis ventas" junto al detalle. */
function Cifra({ etq, v, color, titulo }) {
  return (
    <div title={titulo} style={{ textAlign: "right", minWidth: 92, padding: "3px 7px" }}>
      <span style={{ display: "block", fontSize: 10, color: "#555" }}>{etq}</span>
      <span style={{ display: "block", fontSize: 14, color, fontWeight: 600 }}>
        {fmt(v || 0)}
      </span>
    </div>
  );
}

// ── Llaves de acceso ───────────────────────────────────────────────────────
//
// El camino de vuelta cuando no hay correo que llegue ni teléfono a la mano.
// Vive aquí porque es de la PERSONA, no de la vinícola: quien trabaje con dos
// tiene una llave, no dos.
//
// LO QUE ESTA PANTALLA TIENE QUE LOGRAR es que la llave se guarde de verdad.
// Se enseña UNA vez y no hay forma de volver a verla — así que si el usuario
// cierra el aviso sin copiarla, ya la perdió. Por eso el aviso es grande, dice
// que es la única vez, y no se cierra solo.
function LlavesDeAcceso() {
  const [llaves, setLlaves] = useState([]);
  const [etiqueta, setEtiqueta] = useState("");
  const [nueva, setNueva] = useState(null);      // { llave, prefijo } — solo en memoria
  const [copiada, setCopiada] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [abierto, setAbierto] = useState(false);

  const cargar = useCallback(async () => {
    try { setLlaves(await misLlaves()); } catch (e) { setError(e.message); }
  }, []);
  // Se cargan SIEMPRE, no solo al abrir la sección: a quien no tiene ninguna
  // llave es justamente a quien no se le ocurre abrirla, y es quien más
  // necesita enterarse.
  useEffect(() => { cargar(); }, [cargar]);

  const generar = async () => {
    setOcupado(true); setError("");
    try {
      setNueva(await crearLlave(etiqueta));
      setEtiqueta(""); setCopiada(false);
      await cargar();
    } catch (e) { setError(e.message); }
    setOcupado(false);
  };

  const vivas = llaves.filter(l => !l.revoked_at);
  // Sin ninguna llave viva, esta persona depende de que su sesión sobreviva. Y
  // la sesión vive en el navegador: un teléfono nuevo o un caché borrado la
  // dejan fuera, y entonces la única salida es pedirle otro ticket a quien la
  // dio de alta. Se dice aquí, y se dice cuando importa: si ya tiene una, no se
  // repite el sermón.
  const sinRespaldo = !error && vivas.length === 0;

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2 }}>
          🔐 LLAVES DE ACCESO
          {vivas.length > 0 && (
            <span style={{ marginLeft: 8, color: "#666" }}>
              {vivas.length} activa{vivas.length !== 1 ? "s" : ""}
            </span>
          )}
          {sinRespaldo && (
            <span style={{ marginLeft: 8, color: "#facc15" }}>· ninguna</span>
          )}
        </div>
        <button type="button" onClick={() => setAbierto(a => !a)}
          style={{ ...btnSecondary, padding: "7px 14px", fontSize: 11 }}>
          {abierto ? "Cerrar" : "Ver mis llaves"}
        </button>
      </div>

      <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, marginTop: 10 }}>
        Una llave te deja entrar <b style={{ color: "#888" }}>sin contraseña y sin
        correo</b>, desde cualquier computadora. Guarda una en un lugar seguro: es
        lo que te salva el día que no puedas entrar.
      </div>

      {/* El aviso solo aparece cuando de verdad hay algo que hacer, y desaparece
          en cuanto se hace. Un aviso permanente se vuelve decoración. */}
      {sinRespaldo && (
        <div style={{ marginTop: 12, padding: "12px 14px", background: "#1a1408",
                      border: "1px solid #3a331a", borderRadius: 8,
                      fontSize: 12, color: "#8a8266", lineHeight: 1.7 }}>
          <b style={{ color: "#facc15" }}>No tienes ninguna llave.</b> Tu acceso
          depende de que este navegador no pierda la sesión — un teléfono nuevo o
          una limpieza de memoria te dejarían fuera, y tendrías que pedirle otra
          invitación a quien te dio de alta.
          <button type="button" onClick={() => setAbierto(true)}
            style={{ ...btnSecondary, display: "block", marginTop: 10,
                     padding: "8px 14px", fontSize: 12,
                     borderColor: "#3a5a1a", color: "#a8d060" }}>
            Generar mi llave
          </button>
        </div>
      )}

      {abierto && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #1a1a1a" }}>
          {/* La llave recién creada. Grande, y sin cerrarse sola. */}
          {nueva && (
            <div style={{ background: "#0d1f0d", border: "1px solid #2a5a2a", borderRadius: 8,
                          padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "#a8d060", marginBottom: 8 }}>
                Cópiala ahora. <b>Es la única vez que se puede ver.</b>
              </div>
              <div style={{ fontFamily: "monospace", fontSize: 13, color: "#e8e0d4",
                            background: "#0a0a0a", padding: "10px 12px", borderRadius: 6,
                            wordBreak: "break-all", border: "1px solid #1e1e1e" }}>
                {nueva.llave}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <button type="button"
                  onClick={() => { navigator.clipboard?.writeText(nueva.llave)
                    .then(() => setCopiada(true)).catch(() => {}); }}
                  style={{ ...btnSecondary, padding: "7px 14px", fontSize: 11 }}>
                  {copiada ? "✓ Copiada" : "Copiar"}
                </button>
                <button type="button" onClick={() => { setNueva(null); setCopiada(false); }}
                  style={{ ...btnSecondary, padding: "7px 14px", fontSize: 11,
                           borderColor: "#2a3a2a", color: "#888" }}>
                  Ya la guardé
                </button>
              </div>
              <div style={{ fontSize: 10, color: "#667", marginTop: 10, lineHeight: 1.6 }}>
                No se guarda en ningún lado: en la base solo queda su huella. Si la
                pierdes, revócala y genera otra.
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap",
                        marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 170 }}>
              <Label>Para qué es (opcional)</Label>
              <input value={etiqueta} onChange={e => setEtiqueta(e.target.value)}
                placeholder="la del cajón, mi laptop…" style={inputStyle} />
            </div>
            <button type="button" onClick={generar} disabled={ocupado}
              style={{ ...btnPrimary, padding: "9px 18px", fontSize: 12,
                       opacity: ocupado ? 0.5 : 1 }}>
              {ocupado ? "Generando…" : "Generar una llave"}
            </button>
          </div>

          {error && (
            <div style={{ fontSize: 12, color: "#f87171", marginBottom: 10 }}>⚠️ {error}</div>
          )}

          {llaves.length === 0 && (
            <div style={{ fontSize: 12, color: "#555" }}>Todavía no tienes ninguna.</div>
          )}

          {llaves.map(l => (
            <div key={l.id} style={{ display: "flex", gap: 10, alignItems: "baseline",
                                     padding: "8px 0", borderTop: "1px solid #141414",
                                     fontSize: 12, flexWrap: "wrap",
                                     opacity: l.revoked_at ? 0.45 : 1 }}>
              <span style={{ fontFamily: "monospace", color: "#c9a96e" }}>
                vt_{l.prefijo}…
              </span>
              <span style={{ flex: 1, minWidth: 100, color: "#888" }}>
                {l.etiqueta || "sin nombre"}
              </span>
              <span style={{ fontSize: 10, color: "#555" }}>
                {l.revoked_at
                  ? `revocada ${new Date(l.revoked_at).toLocaleDateString("es-MX")}`
                  : l.last_used_at
                    ? `usada ${new Date(l.last_used_at).toLocaleDateString("es-MX")}`
                    : "nunca usada"}
              </span>
              {!l.revoked_at && (
                <button type="button"
                  onClick={async () => {
                    if (!confirm("Revocar esta llave.\n\nDeja de servir de inmediato y no se puede deshacer.")) return;
                    try { await revocarLlave(l.id); await cargar(); }
                    catch (e) { setError(e.message); }
                  }}
                  style={{ background: "transparent", border: "1px solid #3a2020",
                           borderRadius: 5, color: "#c97070", fontSize: 10,
                           padding: "3px 9px", cursor: "pointer" }}>
                  Revocar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── El PIN ─────────────────────────────────────────────────────────────────
//
// Protege lo que destruye un documento o mueve dinero. Ver AUTENTICACION.md §3.
//
// La sesión es permanente a propósito —quien levanta pedidos todo el día no
// puede estar entrando— y eso tiene una consecuencia que el PIN atiende: quien
// tome el teléfono desbloqueado ES ese vendedor. Ver y producir queda libre;
// cancelar, no.
function MiPin() {
  const [estado, setEstado] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [anterior, setAnterior] = useState("");
  const [uno, setUno] = useState("");
  const [dos, setDos] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);

  const cargar = useCallback(async () => {
    try { setEstado(await estadoPin()); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const puesto = !!estado?.puesto;
  const valido = /^[0-9]{4,8}$/.test(uno) && uno === dos && (!puesto || anterior.length >= 4);

  const guardar = async () => {
    setOcupado(true); setError("");
    try {
      await ponerPin(uno, puesto ? anterior : null);
      setUno(""); setDos(""); setAnterior("");
      setAbierto(false); setListo(true);
      setTimeout(() => setListo(false), 3000);
      await cargar();
    } catch (e) { setError(e.message); }
    setOcupado(false);
  };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2 }}>
          🔢 MI PIN
          <span style={{ marginLeft: 8, color: puesto ? "#a8d060" : "#facc15" }}>
            {puesto ? "puesto" : "sin poner"}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {listo && <span style={{ fontSize: 12, color: "#a8d060" }}>✓ Guardado</span>}
          <button type="button" onClick={() => { setAbierto(a => !a); setError(""); }}
            style={{ ...btnSecondary, padding: "7px 14px", fontSize: 11 }}>
            {abierto ? "Cerrar" : puesto ? "Cambiarlo" : "Poner un PIN"}
          </button>
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, marginTop: 10 }}>
        Se pide para <b style={{ color: "#888" }}>cancelar una remisión</b> y para lo que
        mueve dinero. No para levantar pedidos ni para consultar: eso es todos los días.
        {!puesto && (
          <> <span style={{ color: "#facc15" }}>Sin PIN no vas a poder cancelar.</span></>
        )}
      </div>

      {abierto && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #1a1a1a" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            {puesto && (
              <div style={{ minWidth: 130 }}>
                <Label>PIN actual</Label>
                <input type="password" inputMode="numeric" value={anterior} autoComplete="off"
                  onChange={e => { setAnterior(e.target.value.replace(/\D/g, "")); setError(""); }}
                  style={{ ...inputStyle, letterSpacing: 4, textAlign: "center" }} />
              </div>
            )}
            <div style={{ minWidth: 130 }}>
              <Label>{puesto ? "PIN nuevo" : "PIN"}</Label>
              <input type="password" inputMode="numeric" value={uno} autoComplete="off"
                onChange={e => { setUno(e.target.value.replace(/\D/g, "")); setError(""); }}
                style={{ ...inputStyle, letterSpacing: 4, textAlign: "center" }} />
            </div>
            <div style={{ minWidth: 130 }}>
              <Label>Otra vez</Label>
              <input type="password" inputMode="numeric" value={dos} autoComplete="off"
                onChange={e => { setDos(e.target.value.replace(/\D/g, "")); setError(""); }}
                style={{ ...inputStyle, letterSpacing: 4, textAlign: "center",
                         borderColor: dos && uno !== dos ? "#3a1a1a" : undefined }} />
            </div>
            <button type="button" onClick={guardar} disabled={!valido || ocupado}
              style={{ ...btnPrimary, padding: "9px 18px", fontSize: 12,
                       opacity: (!valido || ocupado) ? 0.4 : 1 }}>
              {ocupado ? "Guardando…" : "Guardar"}
            </button>
          </div>

          <div style={{ fontSize: 10, color: "#556", marginTop: 8, lineHeight: 1.6 }}>
            De 4 a 8 dígitos. No valen los que se adivinan primero —1234, 0000, todos
            iguales—. A los cinco intentos fallidos se bloquea quince minutos.
            {puesto && <> Si lo olvidaste, entra con tu llave de acceso y ponlo de nuevo.</>}
          </div>

          {error && (
            <div style={{ fontSize: 12, color: "#f87171", marginTop: 8 }}>⚠️ {error}</div>
          )}
        </div>
      )}
    </Card>
  );
}
